// =============================================================================
// VandieZ admin-web -- Locations master (/masterdata/locations)
// =============================================================================
// CRUD over the `locations` table (WS-D/D1 Task 6). Backend uniqueness is
// (district, pincode) -- a duplicate insert/update surfaces as a 409 with
// `code: "LOCATION_DUPLICATE"`, which we translate to a friendly message.
//
// No hard delete: the "active" toggle is a PATCH {is_active} against the
// same row, mirroring CommissionRulesPage's soft-deactivate pattern.
//
// Task 7's Zones page reuses `masterdataAdminApi` from api/client.ts and
// follows this same page shape.
// =============================================================================

import { AlertCircle, Download, Loader2, Plus, Search } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { masterdataAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { Location, LocationCreatePayload } from "@/types/masterdata";

/// Translate a caught error into the message shown in the page-level
/// Alert. LOCATION_DUPLICATE gets a friendlier rendering than the raw
/// backend detail string.
function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "LOCATION_DUPLICATE") {
      return "That district/pincode combination already exists.";
    }
    return e.problem.detail;
  }
  return "Unexpected error.";
}

interface CreateLocationFormProps {
  onCreated: () => void;
  onCancel: () => void;
}

function CreateLocationForm({
  onCreated,
  onCancel,
}: CreateLocationFormProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [city, setCity] = useState<string>("");
  const [district, setDistrict] = useState<string>("");
  const [pincode, setPincode] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [lat, setLat] = useState<string>("");
  const [lng, setLng] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (accessToken === null) {
      return;
    }
    if (
      city.trim().length === 0 ||
      district.trim().length === 0 ||
      state.trim().length === 0
    ) {
      setError("City, district, and state are required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: LocationCreatePayload = {
        city: city.trim(),
        district: district.trim(),
        state: state.trim(),
      };
      if (pincode.trim().length > 0) {
        payload.pincode = pincode.trim();
      }
      if (lat.trim().length > 0) {
        payload.lat = lat.trim();
      }
      if (lng.trim().length > 0) {
        payload.lng = lng.trim();
      }
      await masterdataAdminApi.createLocation(accessToken, payload);
      onCreated();
    } catch (e: unknown) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>New location</CardTitle>
        <CardDescription>
          City + district + state are required. Pincode is optional but
          (district, pincode) must be unique. Lat/lng are optional decimal
          coordinates.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="loc-city">City</Label>
            <Input
              id="loc-city"
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
              }}
              placeholder="Velachery"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="loc-district">District</Label>
            <Input
              id="loc-district"
              value={district}
              onChange={(e) => {
                setDistrict(e.target.value);
              }}
              placeholder="Chennai"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="loc-state">State</Label>
            <Input
              id="loc-state"
              value={state}
              onChange={(e) => {
                setState(e.target.value);
              }}
              placeholder="Tamil Nadu"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="loc-pincode">Pincode</Label>
            <Input
              id="loc-pincode"
              value={pincode}
              onChange={(e) => {
                setPincode(e.target.value);
              }}
              placeholder="600042"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="loc-lat">Latitude (optional)</Label>
            <Input
              id="loc-lat"
              value={lat}
              onChange={(e) => {
                setLat(e.target.value);
              }}
              placeholder="12.9756"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="loc-lng">Longitude (optional)</Label>
            <Input
              id="loc-lng"
              value={lng}
              onChange={(e) => {
                setLng(e.target.value);
              }}
              placeholder="80.2207"
              disabled={submitting}
            />
          </div>
        </div>

        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Creating…
              </>
            ) : (
              "Create location"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/// Build a downloadable CSV Blob URL from the currently-loaded rows (no
/// extra round-trip -- same client-side approach used by the dispute /
/// user CSV exports elsewhere in this app).
function downloadLocationsCsv(rows: Location[]): void {
  const header = ["id", "city", "district", "pincode", "state", "lat", "lng", "is_active"];
  const escape = (v: string): string =>
    /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  const csv = [
    header.join(","),
    ...rows.map((r) =>
      [
        r.id,
        r.city,
        r.district,
        r.pincode ?? "",
        r.state,
        r.lat ?? "",
        r.lng ?? "",
        String(r.is_active),
      ]
        .map((c) => escape(c))
        .join(","),
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vandiez-locations-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function LocationsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Committed filters -- read by `load`'s deps below, so a fetch fires
  // only when these change (mount, or "Search" submit).
  const [stateFilter, setStateFilter] = useState<string>("");
  const [districtFilter, setDistrictFilter] = useState<string>("");
  const [qFilter, setQFilter] = useState<string>("");

  // Draft filters -- bound to the inputs (what the user is typing).
  // Mirrors UsersListPage's qDraft/roleDraft/statusDraft split: typing
  // must not fire a request per keystroke, only "Search" commits it.
  const [stateDraft, setStateDraft] = useState<string>("");
  const [districtDraft, setDistrictDraft] = useState<string>("");
  const [qDraft, setQDraft] = useState<string>("");

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await masterdataAdminApi.listLocations(
          accessToken,
          {
            ...(stateFilter.trim() ? { state: stateFilter.trim() } : {}),
            ...(districtFilter.trim() ? { district: districtFilter.trim() } : {}),
            ...(qFilter.trim() ? { q: qFilter.trim() } : {}),
          },
          signal,
        );
        setLocations(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setError(friendlyError(e));
      } finally {
        setLoading(false);
      }
    },
    [accessToken, stateFilter, districtFilter, qFilter],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const onSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    // Commit the drafts. This changes `load`'s deps, which re-runs the
    // effect below and fires exactly one fetch.
    setQFilter(qDraft);
    setStateFilter(stateDraft);
    setDistrictFilter(districtDraft);
  };

  const toggleActive = async (loc: Location) => {
    if (accessToken === null) {
      return;
    }
    setTogglingId(loc.id);
    try {
      await masterdataAdminApi.updateLocation(accessToken, loc.id, {
        is_active: !loc.is_active,
      });
      await load();
    } catch (e: unknown) {
      setError(friendlyError(e));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Locations"
        description="Geographic master data (city / district / state / pincode) backing lane pricing and zones."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                downloadLocationsCsv(locations);
              }}
              disabled={locations.length === 0}
            >
              <Download className="mr-1 h-4 w-4" /> Export CSV
            </Button>
            {!creating && (
              <Button
                onClick={() => {
                  setCreating(true);
                }}
              >
                <Plus className="mr-1 h-4 w-4" /> New location
              </Button>
            )}
          </div>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {creating && (
        <CreateLocationForm
          onCreated={() => {
            setCreating(false);
            void load();
          }}
          onCancel={() => {
            setCreating(false);
          }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Search locations</CardTitle>
          <CardDescription>
            Filter by state / district, or free-text match against city or
            district.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSearch} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="loc-filter-q">City or district</Label>
              <Input
                id="loc-filter-q"
                value={qDraft}
                onChange={(e) => {
                  setQDraft(e.target.value);
                }}
                placeholder="Velachery"
              />
            </div>
            <div className="min-w-[160px]">
              <Label htmlFor="loc-filter-state">Filter by state</Label>
              <Input
                id="loc-filter-state"
                value={stateDraft}
                onChange={(e) => {
                  setStateDraft(e.target.value);
                }}
                placeholder="Tamil Nadu"
              />
            </div>
            <div className="min-w-[160px]">
              <Label htmlFor="loc-filter-district">Filter by district</Label>
              <Input
                id="loc-filter-district"
                value={districtDraft}
                onChange={(e) => {
                  setDistrictDraft(e.target.value);
                }}
                placeholder="Chennai"
              />
            </div>
            <Button type="submit" disabled={loading}>
              <Search className="mr-1 h-4 w-4" /> Search
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Locations ({locations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && locations.length === 0 ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : locations.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No locations match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4">City</th>
                    <th className="py-2 pr-4">District</th>
                    <th className="py-2 pr-4">State</th>
                    <th className="py-2 pr-4">Pincode</th>
                    <th className="py-2 pr-4">Lat</th>
                    <th className="py-2 pr-4">Lng</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map((loc) => (
                    <tr key={loc.id} className="border-b align-top last:border-b-0">
                      <td className="py-3 pr-4 font-medium">{loc.city}</td>
                      <td className="py-3 pr-4">{loc.district}</td>
                      <td className="py-3 pr-4">{loc.state}</td>
                      <td className="py-3 pr-4">{loc.pincode ?? "—"}</td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {loc.lat ?? "—"}
                      </td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {loc.lng ?? "—"}
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className={
                            loc.is_active
                              ? "inline-flex items-center rounded-full bg-success-subtle px-2 py-0.5 text-xs font-semibold text-success-subtle-foreground"
                              : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground"
                          }
                        >
                          {loc.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={togglingId === loc.id}
                          onClick={() => void toggleActive(loc)}
                        >
                          {togglingId === loc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : loc.is_active ? (
                            "Deactivate"
                          ) : (
                            "Activate"
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
